/**
 * CR109: Font Picker Component
 * 
 * Shared dropdown for selecting fonts from the font registry.
 * Used in Theme Labs and Layout Studio.
 */

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { 
  FONT_REGISTRY, 
  getFontById, 
  getRecommendationLabel,
  type FontRegistryItem 
} from "@/lib/theme/fontRegistry";

interface FontPickerProps {
  /** Current selected font ID */
  value?: string | null;
  /** Callback when font changes */
  onChange: (fontId: string) => void;
  /** Optional filter by recommendation */
  filterBy?: "ui" | "headlines" | "both" | "all";
  /** Show sample text preview */
  showPreview?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Size variant */
  size?: "sm" | "default";
  /** Disable the picker */
  disabled?: boolean;
}

export function FontPicker({
  value,
  onChange,
  filterBy = "all",
  showPreview = true,
  placeholder = "Select font...",
  size = "default",
  disabled = false,
}: FontPickerProps) {
  const fonts = filterBy === "all" 
    ? FONT_REGISTRY 
    : FONT_REGISTRY.filter(f => f.recommendedFor === filterBy || f.recommendedFor === "both");
  
  const selectedFont = getFontById(value);

  return (
    <Select value={value ?? undefined} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className={size === "sm" ? "h-8 text-xs" : ""}>
        <SelectValue placeholder={placeholder}>
          {selectedFont && (
            <span style={{ fontFamily: selectedFont.cssFamily }}>
              {selectedFont.label}
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="bg-popover border shadow-md z-50">
        {fonts.map((font) => (
          <SelectItem 
            key={font.id} 
            value={font.id}
            className="cursor-pointer"
          >
            <div className="flex items-center justify-between gap-3 w-full">
              <span 
                style={{ fontFamily: showPreview ? font.cssFamily : undefined }}
                className="flex-1"
              >
                {font.label}
              </span>
              <Badge 
                variant="outline" 
                className="text-[10px] py-0 px-1 shrink-0 font-normal"
              >
                {getRecommendationLabel(font.recommendedFor)}
              </Badge>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Font preview label showing selected font in its own typeface.
 */
export function FontPreviewLabel({ 
  fontId, 
  className = "" 
}: { 
  fontId?: string | null; 
  className?: string;
}) {
  const font = getFontById(fontId);
  if (!font) return null;
  
  return (
    <span 
      className={className}
      style={{ fontFamily: font.cssFamily }}
    >
      {font.label}
    </span>
  );
}
